import { Text, XStack, YStack } from "tamagui";
import KioskIcon, { KioskIconName } from "../KioskIcon";
import { kiosk } from "../kioskTheme";

/**
 * 區段小標題（如「配送資訊」）。
 *
 * 原本是 blueD（#1e88e5）加 800 字重——那個藍是主色階的，用在只負責分段的小標上
 * 會和真正可按的東西搶注意力。小標的工作是「把下面那塊圈起來」，不是被看見；
 * 改成中性灰、字重降到 700，層級才對得起來。
 * 上外距也從 16 收到 8：段與段之間本來就有卡片邊界在分，不需要再靠空白隔第二次。
 */
export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize={13} fontWeight="700" color={kiosk.grey} letterSpacing={0.6} mt={8} mb={7} ml={4}>
    {children}
  </Text>
);

/** 白底圓角卡片容器。 */
export const Card = ({ children }: { children: React.ReactNode }) => (
  <YStack backgroundColor={kiosk.card} borderWidth={1} borderColor={kiosk.line} borderRadius={16} px={14} py={4}>
    {children}
  </YStack>
);

/** 可編輯資訊列：icon + 標籤 + 值（值可為字串或自訂節點）+「變更」。 */
export const EditRow = ({
  icon,
  k,
  v,
  onPress,
  top,
}: {
  icon: KioskIconName;
  k: string;
  v: React.ReactNode;
  onPress?: () => void;
  top?: boolean;
}) => (
  <XStack
    ai="center"
    gap={10}
    py={12}
    borderTopWidth={top ? 1 : 0}
    borderColor="#f0f2f6"
    cursor={onPress ? "pointer" : "default"}
    onPress={onPress}
    pressStyle={onPress ? { opacity: 0.6 } : undefined}>
    <KioskIcon name={icon} size={20} color={kiosk.blue} />
    <YStack flex={1} minWidth={0}>
      <Text fontSize={13} color={kiosk.grey}>
        {k}
      </Text>
      {typeof v === "string" ? (
        <Text fontSize={15.5} fontWeight="700" color={kiosk.ink} mt={1} lineHeight={21}>
          {v}
        </Text>
      ) : (
        v
      )}
    </YStack>
    {onPress && (
      <Text color={kiosk.blue} fontSize={14} fontWeight="700" flexShrink={0} style={{ whiteSpace: "nowrap" } as any}>
        變更
      </Text>
    )}
  </XStack>
);

/**
 * 費用明細的一列。
 *
 * tone="discount" 給折抵用：它和小計、樓層費原本畫成同一個灰色，但那三者的意義相反——
 * 費用是「你要多付」，折抵是「你少付了」。長得一樣的話，客戶不會注意到自己省了錢，
 * 而那正是券最需要被看見的一刻（下單前的最後一眼）。
 * 用綠色 + 加粗 + 一張票券圖示區隔；不加底色塊是刻意的，明細列一旦有人穿上底色，
 * 整張卡的節奏就被打斷，而這裡要的是「這一行不一樣」不是「這一行是另一個區塊」。
 */
export const FeeRow = ({ k, v, tone = "default" }: { k: string; v: string; tone?: "default" | "discount" }) => {
  const isDiscount = tone === "discount";
  const color = isDiscount ? kiosk.green : "#4b5568";
  return (
    <XStack jc="space-between" ai="center" py={5}>
      <XStack ai="center" gap={5} minWidth={0}>
        {isDiscount && <KioskIcon name="ticket" size={14} color={kiosk.green} strokeWidth={2.2} />}
        <Text fontSize={15} fontWeight={isDiscount ? "700" : "400"} color={color}>
          {k}
        </Text>
      </XStack>
      <Text fontSize={isDiscount ? 16 : 15} fontWeight={isDiscount ? "800" : "400"} color={color}>
        {v}
      </Text>
    </XStack>
  );
};

/** 將 yyyy-mm-dd 轉為「今天 / 明天 / M/D（週X）」方便辨識。 */
export const formatDeliveryDate = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);
  const wd = ["日", "一", "二", "三", "四", "五", "六"][d.getDay()];
  const md = `${d.getMonth() + 1}/${d.getDate()}（週${wd}）`;
  if (diffDays === 0) return `今天 ${md}`;
  if (diffDays === 1) return `明天 ${md}`;
  return md;
};
